import type { SupportedLocale } from '@camelot/components'
import * as Sentry from '@sentry/vue'
import { useBrowserLocation } from '@vueuse/core'
import { ref } from 'vue'
import type { TranslationAuthCallbackKeys } from '~/composables/translate/keys/auth-callback-translation-keys'
import type { TranslationCategoryKeys } from '~/composables/translate/keys/category-page-translation-keys'
import type { TranslationExtrasKeys } from '~/composables/translate/keys/extras-page-translation-keys'
import type { TranslationLayoutKeys } from '~/composables/translate/keys/layout-translation-keys'
import type { TranslationLeadKeys } from '~/composables/translate/keys/lead-page-translation-keys'
import type { TranslationOverlayKeys } from '~/composables/translate/keys/overlay-translation-keys'
import type { TranslationPersonalKeys } from '~/composables/translate/keys/personal-page-translation-keys'
import type { TranslationSidebarKeys } from '~/composables/translate/keys/sidebar-translation-keys'
import type { TranslationOnboardingKeys } from '~/composables/translate/keys/webiew-onboarding-translation-keys'
import type { TranslationWebviewPaymentKeys } from '~/composables/translate/keys/webview-payment-translation-keys'
import type { TranslationWebviewSelectKeys } from '~/composables/translate/keys/webview-select-translation-keys'

export const DEFAULT_LOCALE = 'en'
export const SUPPORTED_LOCALES = ['da', 'de', 'en', 'es', 'fr', 'it', 'nl'] as const
export type SupportedLocalesPrebooking = typeof SUPPORTED_LOCALES[number]
export const SUPPORTED_LANGUAGES: Record<SupportedLocalesPrebooking, string> = {
  da: 'Dansk',
  de: 'Deutsch',
  en: 'English',
  es: 'Español',
  fr: 'Français',
  it: 'Italiano',
  nl: 'Nederlands',
}

export interface Replacers {
  [key: string]: string
}

const language = ref<SupportedLocalesPrebooking>(DEFAULT_LOCALE)
const translations = ref<{ [key: string]: string }>({})

const LOCALE_MAP: Record<SupportedLocalesPrebooking, SupportedLocale> = {
  da: 'da-DK',
  de: 'de-DE',
  en: 'en-US',
  es: 'es-ES',
  fr: 'fr-FR',
  it: 'it-IT',
  nl: 'nl-NL',
}

export function useTranslations() {
  return {
    language: readonly(language),
    dateLocale: computed<SupportedLocale>(() => LOCALE_MAP[language.value]),
  }
}

export async function loadTranslations(newLanguage?: string): Promise<boolean> {
  const { value: { origin } } = useBrowserLocation()

  if (!origin)
    return Promise.resolve(false)

  const { app: { baseURL } } = useRuntimeConfig()
  const languageToUse = getLanguageToUse(newLanguage)

  try {
    const { buildId } = useRuntimeConfig().app
    const response = await fetch(`${origin}${baseURL}lang/${languageToUse}.json?v=${buildId}`)
    const data: any = await response.json()
    translations.value = typeof data === 'object' ? data : {}
    language.value = languageToUse
    return true
  }
  catch (error) {
    Sentry.captureException(error)
    console.error('Error while loading translations', error)
    return false
  }
}

function getLanguageToUse(language: string = DEFAULT_LOCALE): SupportedLocalesPrebooking {
  const supportedLocales = SUPPORTED_LOCALES as readonly string[]
  return supportedLocales.includes(language)
    ? language as SupportedLocalesPrebooking
    : DEFAULT_LOCALE
}

export type TranslationKey = TranslationLayoutKeys
  | TranslationCategoryKeys
  | TranslationExtrasKeys
  | TranslationPersonalKeys
  | TranslationLeadKeys
  | TranslationOverlayKeys
  | TranslationSidebarKeys
  | TranslationWebviewSelectKeys
  | TranslationOnboardingKeys
  | TranslationAuthCallbackKeys
  | TranslationWebviewPaymentKeys

export function translate(key: TranslationKey, replacers: Replacers = {}): string {
  const translation = translations.value[key] || key

  if (translation.includes('{country}') && !replacers['{country}'])
    replacers['{country}'] = language.value

  return translation !== key && Object.keys(replacers).length > 0
    ? Object.keys(replacers)
      .reduce((acc, key) => acc.replaceAll(key, replacers[key]), translation)
    : translation
}
